import { useState } from "react";
import { portalApi } from "./PortalShell";
import { KeyRound, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Change password + Sign out everywhere.
 * - "Current password" is only required if the user is NOT on must_change_password.
 *   The backend already enforces this; the frontend hides the field accordingly.
 * - "Sign out everywhere" bumps the backend token_version so every other device
 *   drops to the login screen on its next request.
 */
export default function PortalSecuritySection({ mustChangePassword }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const change = async (e) => {
    e?.preventDefault();
    if (next.length < 10) {
      toast.error("Password must be at least 10 characters");
      return;
    }
    if (next !== confirmPw) {
      toast.error("The two new passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await portalApi.post("/affiliate/auth/change-password", {
        current_password: mustChangePassword ? undefined : current,
        new_password: next,
      });
      toast.success("Password updated. Other devices have been signed out.");
      setCurrent(""); setNext(""); setConfirmPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password");
    } finally {
      setBusy(false);
    }
  };

  const signoutEverywhere = async () => {
    if (!window.confirm("Sign out of every device? You'll need to sign in again here afterwards.")) return;
    try {
      await portalApi.post("/affiliate/auth/signout-everywhere");
      toast.success("Signed out everywhere");
      // The backend has cleared this device's cookies too; the next request will 401.
      window.location.href = "/affiliate/login";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign out");
    }
  };

  return (
    <div data-testid="portal-security-section" className="space-y-4">
      <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Security</div>

      {mustChangePassword && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-[12px] text-amber-200 leading-relaxed">
          <b>Please set a new password.</b> The one your ASM shared is a one-time password — pick your own now so no one else can sign in with it.
        </div>
      )}

      <form onSubmit={change} className="rounded-xl bg-neutral-900 border border-neutral-800 p-4 space-y-3">
        <div className="flex items-start gap-3 pb-2 border-b border-neutral-800">
          <div className="h-9 w-9 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center shrink-0">
            <KeyRound className="h-4 w-4 text-orange-300" />
          </div>
          <div className="text-[12px] text-neutral-300 leading-relaxed">
            Set a new password you'll remember (or let your passkey do the signing in). Changing your password signs you out on every other device.
          </div>
        </div>

        {!mustChangePassword && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Current password</label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="change-pw-current"
              className="mt-1 w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
            />
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">New password</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
            data-testid="change-pw-new"
            className="mt-1 w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
          />
          <div className="text-[10px] text-neutral-500 mt-1">10+ characters. A short phrase works great.</div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Confirm new password</label>
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
            data-testid="change-pw-confirm"
            className="mt-1 w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="change-pw-submit"
          className="inline-flex items-center gap-2 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold"
        >
          <ShieldCheck className="h-4 w-4" />
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>

      <div className="rounded-xl bg-neutral-900 border border-neutral-800 p-4 flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
          <LogOut className="h-4 w-4 text-red-300" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Sign out everywhere</div>
          <div className="text-[12px] text-neutral-400 mt-1 leading-relaxed">
            Lost your phone or shared your login with someone? One tap kicks every device out — including the one you're using now.
          </div>
        </div>
        <button
          onClick={signoutEverywhere}
          data-testid="signout-everywhere"
          className="h-9 px-3 rounded-full border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-200 text-xs font-semibold shrink-0"
        >
          Sign out all
        </button>
      </div>
    </div>
  );
}
